/**
 * MifeLife v2 — Mentor Detail page at /m/:slug.
 *
 * 2-tab layout (Profil / Profesyonel Geçmiş) inspired by MyPrax. Right column
 * holds the booking widget (calendar + time + duration). Reuses iyzico
 * checkout flow from V2MentorsPage.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Star, CheckCircle2, Briefcase, GraduationCap, MapPin, Globe,
  Calendar, Clock, Loader2, ExternalLink, ChevronLeft, Sparkles,
  Linkedin, ShieldCheck, Award, Quote, Users,
} from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import SEO from '../../components/SEO';
import { apiRequest, getToken } from '../../lib/utils';

const COMMISSION_RATE = 0.20; // %20 platform komisyonu, mentör %80 alır

export default function V2MentorDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('profile'); // 'profile' | 'history'

  useEffect(() => {
    setLoading(true);
    apiRequest(`/v2/mentors/by-slug/${encodeURIComponent(slug)}`)
      .then((m) => { setMentor(m); })
      .catch(() => apiRequest(`/v2/mentors/${encodeURIComponent(slug)}`))
      .then((m) => { if (m) setMentor(m); })
      .catch((e) => setErr(e?.message || 'Mentör bulunamadı.'))
      .finally(() => setLoading(false));
    // Funnel attribution: lightweight view ping (ref from ?ref= query).
    const ref = new URLSearchParams(window.location.search).get('ref') || 'direct';
    apiRequest(`/v2/mentors/${encodeURIComponent(slug)}/view`, {
      method: 'POST', body: JSON.stringify({ ref }),
    }).catch(() => {});
  }, [slug]);

  if (loading) return <PageShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div></PageShell>;
  if (err || !mentor) return <PageShell><NotFound err={err} /></PageShell>;

  const matchTone = mentor.ai_match_pct >= 90 ? 'text-violet-300' : mentor.ai_match_pct >= 80 ? 'text-amber-300' : 'text-rose-300';

  return (
    <PageShell>
      <MentorSeo mentor={mentor} slug={slug} />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-24 pb-20" data-testid="v2-mentor-detail">
        <Link to="/mentorler" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition mb-6" data-testid="v2-mentor-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Tüm mentörler
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* LEFT — main content */}
          <div className="min-w-0">
            <HeaderCard mentor={mentor} matchTone={matchTone} />
            <CredibilityStrip mentor={mentor} />
            <Tabs tab={tab} setTab={setTab} />
            {tab === 'profile' ? <ProfileTab mentor={mentor} /> : <HistoryTab mentor={mentor} />}
          </div>

          {/* RIGHT — booking widget (sticky on desktop) */}
          <aside className="lg:sticky lg:top-24 h-fit">
            <BookingWidget mentor={mentor} onLoginRequired={() => nav('/kayit?after=mentor')} />
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

// ============================================================================

// ---------- SEO: meta tags + JSON-LD Person schema ----------
const MentorSeo = ({ mentor, slug }) => {
  const desc = (mentor.bio_short || mentor.bio_full || `${mentor.name} — ${mentor.title}`)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
  const title = `${mentor.name} · ${mentor.title} | MifeLife Mentör`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: mentor.name,
    jobTitle: mentor.title,
    description: desc,
    image: mentor.photo_url,
    url: `https://mifelife.com/m/${slug}`,
    knowsAbout: mentor.specialties || [],
    ...(mentor.city && { address: { '@type': 'PostalAddress', addressLocality: mentor.city } }),
    ...(mentor.review_count > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: mentor.rating,
        reviewCount: mentor.review_count,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };

  return (
    <SEO
      title={title}
      description={desc}
      path={`/m/${slug}`}
      image={mentor.photo_url}
      jsonLd={jsonLd}
    />
  );
};

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-[#0F0F1A] text-white antialiased">
    <V2Nav lang="tr" />
    {children}
    <V2Footer lang="tr" />
  </div>
);

const NotFound = ({ err }) => (
  <div className="max-w-2xl mx-auto px-6 pt-32 pb-20 text-center">
    <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">404</div>
    <h1 className="text-3xl font-semibold tracking-tight mb-3">Mentör bulunamadı.</h1>
    <p className="text-sm text-slate-400 mb-6">{err || 'Aradığın mentör platformda görünmüyor.'}</p>
    <Link to="/mentorler" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">Tüm mentörler</Link>
  </div>
);

// ---------- Header card ----------
const HeaderCard = ({ mentor, matchTone }) => (
  <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6 sm:p-8 mb-6">
    <div className="flex flex-col sm:flex-row gap-6">
      <img src={mentor.photo_url} alt={mentor.name} className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl object-cover flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{mentor.name}</h1>
          {mentor.verified && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-400/40 text-[11px] font-semibold text-violet-200" title="Kimliği ve geçmişi MifeLife tarafından doğrulandı" data-testid="v2-mentor-verified">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.2} /> Doğrulanmış mentör
            </span>
          )}
        </div>
        <div className="text-base text-slate-300 mb-1">{mentor.title}</div>
        {Array.isArray(mentor.experience) && mentor.experience[0] && (
          <div className="text-[13px] text-slate-400 mb-3 inline-flex items-center gap-1.5" data-testid="v2-mentor-current-role">
            <Briefcase className="w-3.5 h-3.5 text-violet-300" /> {mentor.experience[0].role} <span className="text-violet-300">@ {mentor.experience[0].company}</span>
          </div>
        )}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {mentor.city}</span>
          {mentor.years_experience != null && (
            <>
              <span className="text-slate-600">·</span>
              <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {mentor.years_experience} yıl deneyim</span>
            </>
          )}
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {(mentor.languages || []).map((l) => l.toUpperCase()).join(' · ')}</span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-fuchsia-500/12 border border-fuchsia-400/25 text-fuchsia-200 text-[9px] font-semibold uppercase tracking-wide" title="Farklı dilde görüşme için canlı çeviri (Beta)" data-testid="v2-mentor-global-beta">Global · Beta</span>
          <span className="text-slate-600">·</span>
          <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {mentor.rating} <span className="text-slate-500">({mentor.review_count})</span></span>
        </div>
        <p className="text-[14px] text-slate-300 leading-relaxed mb-4">{mentor.bio_full || mentor.bio_short}</p>
        <div className="flex flex-wrap gap-1.5">
          {(mentor.specialties || []).map((s) => (
            <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-slate-200">{s}</span>
          ))}
        </div>
        {(mentor.linkedin_url || mentor.website_url) && (
          <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="v2-mentor-links">
            {mentor.linkedin_url && (
              <a href={mentor.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-violet-200 transition" data-testid="v2-mentor-linkedin">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
            )}
            {mentor.website_url && (
              <a href={mentor.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-violet-200 transition" data-testid="v2-mentor-website">
                <Globe className="w-3.5 h-3.5" /> Website
              </a>
            )}
          </div>
        )}
      </div>
      {mentor.ai_match_pct != null && (
        <div className="hidden md:flex flex-col items-center justify-center px-5 py-4 rounded-2xl border border-violet-400/20 bg-violet-500/[0.04] min-w-[110px]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">AI Uyum</div>
          <div className={`text-3xl font-semibold ${matchTone}`}>%{mentor.ai_match_pct}</div>
        </div>
      )}
    </div>
  </div>
);

// ---------- Tabs ----------
const Tabs = ({ tab, setTab }) => (
  <div className="border-b border-white/[0.08] mb-6" data-testid="v2-mentor-tabs">
    <div className="flex gap-1">
      {[
        ['profile', 'Profil'],
        ['history', 'Profesyonel Geçmiş'],
      ].map(([k, label]) => (
        <button
          key={k}
          onClick={() => setTab(k)}
          className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === k ? 'border-violet-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          data-testid={`v2-mentor-tab-${k}`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
);

// ---------- Credibility metric strip ----------
const CredibilityStrip = ({ mentor }) => {
  const items = [
    mentor.years_experience != null && { icon: Briefcase, label: 'Deneyim', value: `${mentor.years_experience} yıl` },
    { icon: Users, label: 'Tamamlanan seans', value: mentor.completed_sessions ?? 0 },
    { icon: Star, label: 'Puan', value: `${mentor.rating ?? '—'}${mentor.review_count ? ` (${mentor.review_count})` : ''}` },
    { icon: ShieldCheck, label: 'Durum', value: mentor.verified ? 'Doğrulandı' : 'Aktif' },
  ].filter(Boolean);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="v2-mentor-credibility">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
          <it.icon className="w-4 h-4 text-violet-300 mb-2" strokeWidth={1.8} />
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-0.5">{it.label}</div>
          <div className="text-base font-semibold text-white tabular-nums">{it.value}</div>
        </div>
      ))}
    </div>
  );
};

// ---------- Connections & verification card ----------
const ConnectionsCard = ({ mentor }) => {
  const hasAny = mentor.linkedin_url || mentor.website_url || (Array.isArray(mentor.experience) && mentor.experience[0]);
  if (!hasAny) return null;
  return (
    <Section title="Bağlantılar & Doğrulama" icon={<ShieldCheck className="w-4 h-4" />}>
      <div className="space-y-2.5" data-testid="v2-mentor-connections">
        {mentor.verified && (
          <div className="flex items-center gap-2 text-[13px] text-violet-200">
            <ShieldCheck className="w-4 h-4 text-violet-300" strokeWidth={2} /> Kimlik ve profesyonel geçmiş MifeLife tarafından doğrulandı.
          </div>
        )}
        {Array.isArray(mentor.experience) && mentor.experience[0] && (
          <div className="flex items-center gap-2 text-[13px] text-slate-300">
            <Award className="w-4 h-4 text-amber-300" /> Güncel: <span className="text-white font-medium">{mentor.experience[0].role}</span> @ {mentor.experience[0].company}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {mentor.linkedin_url && (
            <a href={mentor.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#0A66C2]/15 border border-[#0A66C2]/40 text-[#9cc6f5] text-xs font-medium hover:bg-[#0A66C2]/25 transition" data-testid="v2-mentor-linkedin">
              <Linkedin className="w-3.5 h-3.5" /> LinkedIn profili <ExternalLink className="w-3 h-3 opacity-70" />
            </a>
          )}
          {mentor.website_url && (
            <a href={mentor.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white/[0.04] border border-white/15 text-slate-200 text-xs font-medium hover:bg-white/[0.08] transition" data-testid="v2-mentor-website">
              <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3 opacity-70" />
            </a>
          )}
        </div>
      </div>
    </Section>
  );
};

// ---------- Reviews (real, from v2_reviews) ----------
const ReviewsSection = ({ mentorId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiRequest(`/v2/reviews/mentor/${mentorId}`)
      .then(setData)
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [mentorId]);
  if (loading) return null;
  const items = data?.items || [];
  return (
    <Section title="Değerlendirmeler" icon={<Star className="w-4 h-4" />}>
      <div className="flex items-center gap-2 mb-4" data-testid="v2-mentor-reviews-summary">
        <span className="inline-flex items-center gap-1 text-lg font-semibold text-amber-200"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{(data?.rating ?? 0).toFixed(1)}</span>
        <span className="text-xs text-slate-500">{data?.review_count ?? 0} değerlendirme</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="v2-mentor-reviews-empty">Bu mentör henüz yazılı yorum almadı. İlk yorumu sen bırakabilirsin.</div>
      ) : (
        <ul className="space-y-4" data-testid="v2-mentor-reviews-list">
          {items.map((r) => (
            <li key={r.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-200">{r.user_name || 'MifeLife kullanıcısı'}</span>
                <span className="inline-flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-3.5 h-3.5 ${(r.rating || 0) >= s ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />)}
                </span>
              </div>
              {r.review && <p className="text-[13px] text-slate-300 leading-relaxed flex gap-1.5"><Quote className="w-3.5 h-3.5 text-slate-600 shrink-0 mt-0.5" />{r.review}</p>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

// ---------- Profile tab ----------
const ProfileTab = ({ mentor }) => (
  <div className="space-y-5">
    {mentor.session_about && (
      <Section title="Seans Hakkında" icon={<Sparkles className="w-4 h-4" />}>
        <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{mentor.session_about}</p>
      </Section>
    )}
    <Section title="Hakkımda">
      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{mentor.bio_full}</p>
    </Section>
    <ConnectionsCard mentor={mentor} />
    <ReviewsSection mentorId={mentor.id} />
  </div>
);

// ---------- History tab ----------
const HistoryTab = ({ mentor }) => (
  <div className="space-y-6">
    <Section title="Profesyonel Deneyim" icon={<Briefcase className="w-4 h-4" />}>
      {(!mentor.experience || mentor.experience.length === 0) ? (
        <div className="text-sm text-slate-500">Henüz iş geçmişi eklenmemiş.</div>
      ) : (
        <ol className="space-y-4" data-testid="v2-mentor-experience">
          {mentor.experience.map((e, i) => (
            <li key={i} className="border-l-2 border-violet-400/30 pl-4">
              <div className="flex items-baseline gap-2 flex-wrap mb-0.5">
                <span className="text-sm font-medium text-white">{e.role}</span>
                <span className="text-xs text-violet-300">@ {e.company}</span>
              </div>
              <div className="text-[11px] text-slate-500 mb-1">{e.period}</div>
              {e.notes && <p className="text-[13px] text-slate-400 leading-relaxed">{e.notes}</p>}
            </li>
          ))}
        </ol>
      )}
    </Section>
    <Section title="Eğitim" icon={<GraduationCap className="w-4 h-4" />}>
      {(!mentor.education || mentor.education.length === 0) ? (
        <div className="text-sm text-slate-500">Henüz eğitim bilgisi eklenmemiş.</div>
      ) : (
        <ol className="space-y-3" data-testid="v2-mentor-education">
          {mentor.education.map((e, i) => (
            <li key={i} className="border-l-2 border-cyan-400/30 pl-4">
              <div className="text-sm font-medium text-white">{e.degree}</div>
              <div className="text-xs text-slate-400">{e.school} · {e.year}</div>
            </li>
          ))}
        </ol>
      )}
    </Section>
  </div>
);

const Section = ({ title, icon, children }) => (
  <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-3">
      {icon} {title}
    </div>
    {children}
  </div>
);

// ============================================================================
// BOOKING WIDGET (calendar + duration + iyzico checkout)
// ============================================================================
const BookingWidget = ({ mentor, onLoginRequired }) => {
  const authed = !!getToken();
  const durations = mentor.default_session_durations?.length ? mentor.default_session_durations : [30, 60, 90];
  const priceFor = (d) => {
    const p = (mentor.pricing || []).find((x) => x.duration_min === d);
    return p ? p.price_try : Math.round((mentor.hourly_rate_try * d) / 60);
  };
  const [duration, setDuration] = useState(durations.includes(60) ? 60 : durations[0]);
  const [slots, setSlots] = useState([]);
  const [vacation, setVacation] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [date, setDate] = useState(null);   // 'YYYY-MM-DD'
  const [time, setTime] = useState(null);    // 'HH:MM'
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!authed) return;
    apiRequest(`/v2/sessions/quote/${mentor.id}`).then(setQuote).catch(() => {});
  }, [mentor.id, authed]);

  useEffect(() => {
    setSlotsLoading(true);
    apiRequest(`/v2/sessions/slots/${mentor.id}?days=21`)
      .then((r) => { setSlots(r?.slots || []); setVacation(!!r?.vacation_mode); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [mentor.id]);

  // Group available slots by date → [{date, day_label, day_num, times:[]}]
  const days = useMemo(() => {
    const acc = {};
    slots.forEach((s) => {
      acc[s.date] = acc[s.date] || { date: s.date, day_label: s.day_label, times: [] };
      acc[s.date].times.push(s.time);
    });
    return Object.values(acc).map((d) => ({ ...d, day_num: new Date(d.date).getDate(), times: d.times.sort() }));
  }, [slots]);

  const qd = quote?.quotes?.find((x) => x.duration_min === duration);
  const discounted = quote?.eligible && qd && qd.final_price < qd.base_price;
  const baseGross = qd ? qd.base_price : priceFor(duration);
  const gross = discounted ? qd.final_price : baseGross;
  const platform = Math.round(gross * COMMISSION_RATE);
  const mentorNet = gross - platform;

  const submit = async () => {
    if (!date || !time) return;
    if (!authed) {
      try { sessionStorage.setItem('v2_book_intent', JSON.stringify({ mentor_id: mentor.id, slot: { date, time }, duration_min: duration })); } catch (e) { /* storage may be unavailable */ }
      onLoginRequired();
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      const res = await apiRequest('/v2/sessions/checkout', {
        method: 'POST',
        body: JSON.stringify({ mentor_id: mentor.id, date, time, duration_min: duration }),
      });
      if (res?.success && res?.paymentPageUrl) {
        window.location.href = res.paymentPageUrl;
        return;
      }
      // Sandbox/dev fallback — skip iyzico, create a paid session directly.
      const session = await apiRequest('/v2/sessions/book', {
        method: 'POST',
        body: JSON.stringify({ mentor_id: mentor.id, date, time, duration_min: duration, skip_payment: true }),
      });
      setConfirmed(session);
    } catch (e) {
      setErr(e?.message || 'Bir hata oluştu.');
      // A 409 means the slot just got taken — refresh availability.
      if (e?.status === 409) {
        apiRequest(`/v2/sessions/slots/${mentor.id}?days=21`).then((r) => { setSlots(r?.slots || []); setDate(null); setTime(null); }).catch(() => {});
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.06] p-6 text-center" data-testid="v2-booking-confirmed">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-1">Rezervasyonun oluşturuldu</h3>
        <p className="text-xs text-slate-400 mb-4">{new Date(confirmed.scheduled_at).toLocaleString('tr-TR')}</p>
        {confirmed.jitsi_link && (
          <a href={confirmed.jitsi_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition" data-testid="v2-booking-jitsi">
            Jitsi seansa katıl <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="v2-booking-widget">
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          {discounted && <span className="text-base text-slate-500 line-through tabular-nums">₺{baseGross}</span>}
          <span className={`text-2xl font-semibold tabular-nums ${discounted ? 'text-emerald-300' : ''}`}>₺{gross}</span>
        </div>
        <span className="text-xs text-slate-500">{duration} dk</span>
      </div>
      {discounted && (
        <div className="inline-flex items-center gap-1 mb-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200" data-testid="v2-detail-discount">
          <Sparkles className="w-3 h-3" />İlk seans indirimi %{quote.discount_pct}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mb-4">
        Mentöre ₺{mentorNet} · Platform komisyonu ₺{platform} (%{Math.round(COMMISSION_RATE * 100)})
      </div>

      {/* Duration */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Süre</div>
        <div className="flex gap-2">
          {durations.map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`flex-1 flex flex-col items-center justify-center py-2 text-xs rounded-lg border transition ${duration === d ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20'}`}
              data-testid={`v2-booking-dur-${d}`}
            >
              <span className="font-medium">{d} dk</span>
              <span className="text-[10px] tabular-nums opacity-70">₺{priceFor(d)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Day */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> Gün</div>
        {slotsLoading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 text-violet-300 animate-spin" /></div>
        ) : vacation ? (
          <div className="rounded-lg border border-amber-400/30 bg-amber-500/[0.06] p-3 text-xs text-amber-200" data-testid="v2-booking-vacation">
            Bu mentör şu anda izinde. Yakında tekrar müsait olacak.
          </div>
        ) : days.length === 0 ? (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-400" data-testid="v2-booking-no-slots">
            Şu an uygun saat yok. Lütfen daha sonra tekrar dene.
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1.5" data-testid="v2-booking-calendar">
            {days.slice(0, 14).map((d) => {
              const dt = new Date(d.date);
              const dn = ['Pzr', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'][dt.getDay()];
              const sel = date === d.date;
              return (
                <button
                  key={d.date}
                  onClick={() => { setDate(d.date); setTime(null); }}
                  className={`flex flex-col items-center py-1.5 rounded-md text-[10px] border transition ${sel ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/[0.06] bg-white/[0.02] text-slate-400 hover:border-white/20 hover:text-white'}`}
                  data-testid={`v2-booking-day-${d.date}`}
                >
                  <span className="text-slate-500">{dn}</span>
                  <span className="text-sm font-semibold">{d.day_num}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Hours */}
      {date && (
        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Saat</div>
          <div className="grid grid-cols-3 gap-1.5">
            {(days.find((dd) => dd.date === date)?.times || []).map((t) => (
              <button
                key={t}
                onClick={() => setTime(t)}
                className={`h-9 text-xs rounded-md border transition ${time === t ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/[0.06] bg-white/[0.02] text-slate-300 hover:border-white/20'}`}
                data-testid={`v2-booking-time-${t.replace(':', '')}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {err && <div className="text-xs text-rose-400 mb-3">{err}</div>}

      <button
        onClick={submit}
        disabled={!date || !time || submitting}
        className="w-full h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
        data-testid="v2-booking-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (authed ? 'Ödemeye geç →' : 'Giriş yap & rezervasyon')}
      </button>
      <p className="text-[10px] text-slate-500 text-center mt-3 leading-relaxed">
        Güvenli ödeme · iyzico altyapısı<br />24 saat öncesine kadar ücretsiz iptal
      </p>
    </div>
  );
};
